import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Themes from '../../components/Section/themes';
import Questions from '../../components/Section/questions';
import Submission from '../../components/Section/submission';
import useTrackEvent from '../../utils/trackEvent';
import { usePreferences } from '../../context/PreferencesContext';
import type { Question, Answer, ThemeResult } from '../../types/questionnaire';

type TreeNode = Question | Answer;

function Form() {
  const { resetQuestionnaire, setResetQuestionnaire } = usePreferences();

  const getQuestionnaire = () => window.customer.questionnaireData!;
  const trackEvent = useTrackEvent();
  const navigateTo = useNavigate();
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [rootQuestion, setRootQuestion] = useState<Question>(getQuestionnaire());
  const [selectedThemes, setSelectedThemes] = useState<Answer[]>([]);
  const [wrappedThemes, setWrappedThemes] = useState<Answer[]>([]);

  useEffect(() => {
    if (resetQuestionnaire) {
      setResetQuestionnaire(false);
      setSelectedThemes([]);
      setWrappedThemes([]);
      for (const theme of rootQuestion.answers) {
        uncheckAllChildren(theme);
      }
    }
  }, [resetQuestionnaire]);

  useEffect(() => {
    const hasChecked = rootQuestion.answers.some((theme) => theme.checked);
    if (!hasChecked) setSelectedThemes([]);
  }, [rootQuestion]);

  const uncheckAllChildren = (node: TreeNode) => {
    if ('answers' in node) {
      for (const subQuestion of node.answers) {
        uncheckAllChildren(subQuestion);
      }
    } else if ('questions' in node && node.questions) {
      node.checked = false;
      for (const subQuestion of node.questions) {
        uncheckAllChildren(subQuestion);
      }
    }
  };

  const toggleCallback = (answer: Answer, answerId: number): boolean => {
    if (answer.id === answerId) {
      answer.checked = !answer.checked;
      if (!answer.checked) uncheckAllChildren(answer);
      return true;
    }
    return false;
  };

  const recursiveApply = (question: Question | null | undefined, callback: (answer: Answer, answerId: number) => boolean, answerId: number) => {
    if (question && question.answers) {
      for (const answer of question.answers) {
        if (callback(answer, answerId)) return;
        if (answer.questions) {
          for (const subQuestion of answer.questions) {
            recursiveApply(subQuestion, callback, answerId);
          }
        }
      }
    }
  };

  const toggle = (answerId: number) => {
    trackEvent('Select_Question', { select: answerId });
    setRootQuestion((oldRootQuestion) => {
      const newRootQuestion = { ...oldRootQuestion };
      recursiveApply(newRootQuestion, toggleCallback, answerId);
      return newRootQuestion;
    });
  };

  const falseCallback = (answer: Answer): boolean => { answer.checked = false; return false; };

  const checkCallback = (answer: Answer): boolean => {
    if (!answer.checked && answer.questions) {
      for (const subQuestion of answer.questions) {
        recursiveApply(subQuestion, falseCallback, 0);
      }
    }
    return false;
  };

  const createResponse = (question: Question | null | undefined, response: ThemeResult[]) => {
    if (question && question.answers) {
      for (const answer of question.answers) {
        const products = Array.isArray(answer.products) ? answer.products : [];
        const content = Array.isArray(answer.content) ? answer.content : [];
        if (answer.checked && (products.length > 0 || content.length > 0) && answer.theme !== null) {
          const existingTheme = response.find((item) => item.theme === answer.theme);
          if (existingTheme) {
            existingTheme.advice.products = [...new Set([...existingTheme.advice.products, ...products])];
            existingTheme.advice.content = [...new Set([...existingTheme.advice.content, ...content])];
          } else {
            response.push({ theme: answer.theme!, advice: { products, content } });
          }
        }
        if (answer.questions) {
          for (const subQuestion of answer.questions) {
            createResponse(subQuestion, response);
          }
        }
      }
    }
  };

  const submitAndGoToResultPage = () => {
    const results = { ...rootQuestion };
    recursiveApply(results, checkCallback, 0);
    const response: ThemeResult[] = [];
    createResponse(results, response);
    trackEvent('Click_RequestAdvice', {});
    const encodeResponseToParams = (response: ThemeResult[]) =>
      response.map((item, index) => {
        const themeParam = `theme${index}=${encodeURIComponent(item.theme)}`;
        const productsParam = `products${index}=${encodeURIComponent(item.advice.products.join(','))}`;
        const contentParam = `content${index}=${encodeURIComponent(item.advice.content.join(','))}`;
        return `${themeParam}&${productsParam}&${contentParam}`;
      }).join('&');
    const urlParams = encodeResponseToParams(response);
    navigateTo(`/results?${urlParams}`);
  };

  const addRef = (element: HTMLDivElement | null) => {
    if (element && !sectionRefs.current.includes(element)) {
      sectionRefs.current.push(element);
    }
  };

  const scrollToSection = (index: number) => {
    sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submitThemes = () => {
    const selected = rootQuestion.answers.filter((theme) => theme.checked);
    setSelectedThemes(selected);
    setTimeout(() => { scrollToSection(0); }, 100);
  };

  return (
    <div className="background-header">
      <div className="site-container">
        <div className="sections space-y-5 lg:space-y-6">
          <Themes
            themes={rootQuestion}
            onChecked={toggle}
            selectedThemes={selectedThemes}
            wrappedThemes={wrappedThemes}
            setWrappedThemes={setWrappedThemes}
            submitThemes={submitThemes}
          />
          {rootQuestion.answers.map((theme) => (
            <div key={theme.id} ref={(el) => addRef(el)}>
              <Questions
                theme={theme}
                onChecked={toggle}
                selectedThemes={selectedThemes}
                wrappedThemes={wrappedThemes}
                setWrappedThemes={setWrappedThemes}
              />
            </div>
          ))}
          <Submission
            themes={rootQuestion}
            onSubmit={submitAndGoToResultPage}
            selectedThemes={selectedThemes}
            wrappedThemes={wrappedThemes}
            isLoading={false}
            error={null}
          />
        </div>
      </div>
    </div>
  );
}

export default Form;
